import { type FormApi, useForm } from "@tanstack/react-form"
import { type MutationMeta, useMutation } from "@tanstack/react-query"
import { useId } from "react"
import type * as z from "zod"

import { useCaptchaToken } from "@repo/captcha/use-captcha-token"
import type { TypedFormApi } from "@repo/ui/components/ui/form"
import { useStableFunction } from "@repo/ui/hooks/use-stable-function"

import { reportFormException } from "./exception-reporter"

type ServerFunction<Input, Output> = (args: { data: Input }) => Promise<Output>

type SchemaInput<FormSchema extends z.ZodType<object, object>> =
  z.input<FormSchema>
type SchemaOutput<FormSchema extends z.ZodType<object, object>> =
  z.output<FormSchema>
type OutputAssignableToInput<FormSchema extends z.ZodType<object, object>> =
  SchemaOutput<FormSchema> extends SchemaInput<FormSchema> ? FormSchema : never
type CaptchaTokenInput = {
  captchaToken?: string
}

export type UseServerFunctionFormOptions<
  InputData extends object,
  OutputData extends object,
  MutationOutput = unknown,
> = {
  formId?: string
  defaultValues: NoInfer<Partial<InputData>>
  useCaptcha?: InputData extends CaptchaTokenInput ? boolean : false | undefined
  mutationMeta?: MutationMeta
  onSubmit?: (data: OutputData) => void | OutputData
  /**
   * @param result - The result of the server function. (It can be `undefined` if the serverFn threw a `redirect` response)
   * @param variables - The variables passed to the server function.
   * @returns - The result of the server function.
   */
  onSuccess?: (
    result: MutationOutput | undefined,
    variables: { data: InputData },
  ) => void
  onError?: (error: Error) => void
  onInvalid?: (errors: Record<string, string[]>) => void
}

/**
 * Hook for handling forms that submit data to a Tanstack Start server function.
 *
 * Type safety is derived from the Zod schema's output type (`z.infer`), which
 * determines the form data shape. The server function is loosely typed for its
 * input because it has its own `validator` that handles validation — the
 * schema parameter is the single source of truth for both client-side validation
 * and the form's TypeScript types.
 *
 * @param serverFn - A Tanstack Start server function created with createServerFn
 * @param schema - A Zod schema that validates the form data. Its output type (`z.infer`) drives the form's type safety.
 * @param options - Configuration options for the form and callbacks
 */
export const useServerFunctionForm = <
  FormSchema extends z.ZodType<object, object>,
  FnOutput = unknown,
>(
  serverFn: ServerFunction<SchemaInput<FormSchema>, FnOutput>,
  schema: OutputAssignableToInput<FormSchema>,
  options: UseServerFunctionFormOptions<
    SchemaInput<FormSchema>,
    SchemaOutput<FormSchema> & SchemaInput<FormSchema>,
    FnOutput
  >,
) => {
  type FormInputData = SchemaInput<FormSchema>
  type FormOutputData = SchemaOutput<FormSchema>

  const id = useId()
  const { getCaptchaTokenAsync, resetCaptchaToken } = useCaptchaToken()

  const formMutation = useMutation({
    mutationFn: serverFn,
    meta: options.mutationMeta,
  })

  const form = useForm({
    defaultValues: options.defaultValues,
    validators: {
      onSubmit: schema,
    },
    onSubmit: async ({ value }) => {
      const parsedValue = schema.parse(value) as FormOutputData & FormInputData

      const captchaToken = options.useCaptcha
        ? await getCaptchaTokenAsync()
        : undefined

      if (options.useCaptcha && !captchaToken) {
        reportFormException(
          new Error("Captcha token is required in useServerFunctionForm"),
          {
            formId: options.formId ?? id,
          },
        )
      }

      const updatedData: FormOutputData & FormInputData =
        options.onSubmit?.(parsedValue) ?? parsedValue
      const payload: FormInputData =
        options.useCaptcha && captchaToken
          ? ({ ...updatedData, captchaToken } as FormInputData)
          : updatedData

      formMutation.mutate(
        { data: payload },
        {
          onSuccess(result, variables) {
            options.onSuccess?.(result, variables)
          },
          onError(error) {
            options.onError?.(error)
          },
          onSettled() {
            if (options.useCaptcha) {
              resetCaptchaToken()
            }
          },
        },
      )
    },
    onSubmitInvalid: ({ formApi }) => {
      // Convert TanStack Form errors to a simpler format for the callback
      const errors: Record<string, string[]> = {}
      for (const [key, value] of Object.entries(formApi.state.fieldMeta)) {
        // Skip form-level errors (empty key) — only report field-specific ones
        if (!key) continue
        const fieldMeta = value as { errors?: unknown[] }
        if (Array.isArray(fieldMeta.errors) && fieldMeta.errors.length > 0) {
          errors[key] = fieldMeta.errors.flatMap((e: unknown) => {
            if (typeof e === "string") return e
            // Handle StandardSchemaV1Issue objects from Zod/Valibot
            if (e && typeof e === "object" && "message" in e) {
              return (e as { message: string }).message
            }
            // Handle arrays of issues (e.g. from union errors)
            if (Array.isArray(e)) {
              return e
                .filter(
                  (item): item is { message: string } =>
                    item && typeof item === "object" && "message" in item,
                )
                .map((item) => item.message)
            }
            return String(e)
          })
        }
      }
      options.onInvalid?.(errors)
    },
  })

  const handleFormSubmit = useStableFunction((e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    form.handleSubmit()
  })

  const resetFormAndMutation = useStableFunction((values?: FormOutputData) => {
    form.reset(values)
    formMutation.reset()
  })

  const formId = options.formId ?? id

  // Erase specific validator types — consumers only need TFormData for field access.
  const typedForm = form as unknown as TypedFormApi<FormOutputData>

  return {
    form: typedForm,
    formId,
    formMutation,
    isValid: form.state.isValid,
    handleFormSubmit,
    resetFormAndMutation,
  }
}

// Re-export FormApi type for consumers who need to type their form props
export type { FormApi }
